import { create } from "zustand";
import { toast } from "sonner";

import { supabase } from "@/supabaseClient";
import { InvoiceItemType, DialogType, ProductType, ClientType, InvoiceType } from "@/types";
import { StoreType } from "@/store.types";
import {
  createDriver as apiCreateDriver,
  updateDriver as apiUpdateDriver,
  deleteDriver as apiDeleteDriver,
  createTransport as apiCreateTransport,
  updateTransport as apiUpdateTransport,
  deleteTransport as apiDeleteTransport,
  createWaybill as apiCreateWaybill,
  updateWaybill as apiUpdateWaybill,
  deleteWaybill as apiDeleteWaybill,
} from "@/api";

export const useStore = create<StoreType>((set, getStore) => ({
  products: [],
  tenant: null,
  invoices: [],
  clients: [],
  drivers: [],
  transports: [],
  waybills: [],
  user: null,
  isCreateInvoiceLoading: false,
  hasCreateInvoiceError: false,
  createInvoiceError: null,
  isUpdateInvoiceLoading: false,
  hasUpdateInvoiceError: false,
  updateInvoiceError: null,
  isUpsertClientLoading: false,
  hasUpsertClientError: false,
  upsertClientError: null,
  isRemoveInvoiceLoading: false,
  hasRemoveInvoiceError: false,
  removeInvoiceError: null,
  isUpdateTenantLoading: false,
  hasUpdateTenantError: false,
  updateTenantError: null,
  isUpsertProductLoading: false,
  hasUpsertProductError: false,
  upsertProductError: null,
  isCreateDriverLoading: false,
  hasCreateDriverError: false,
  createDriverError: null,
  isUpdateDriverLoading: false,
  hasUpdateDriverError: false,
  updateDriverError: null,
  isDeleteDriverLoading: false,
  hasDeleteDriverError: false,
  deleteDriverError: null,
  isCreateTransportLoading: false,
  hasCreateTransportError: false,
  createTransportError: null,
  isUpdateTransportLoading: false,
  hasUpdateTransportError: false,
  updateTransportError: null,
  isDeleteTransportLoading: false,
  hasDeleteTransportError: false,
  deleteTransportError: null,
  isCreateWaybillLoading: false,
  hasCreateWaybillError: false,
  createWaybillError: null,
  isUpdateWaybillLoading: false,
  hasUpdateWaybillError: false,
  updateWaybillError: null,
  isDeleteWaybillLoading: false,
  hasDeleteWaybillError: false,
  deleteWaybillError: null,
  dialogs: [],
  reset: async () => {
    try {
      const { error } = await supabase.from("invoice_items").delete().neq("id", 0);
      if (error) {
        toast.error(error.message);
        throw error;
      }

      toast.success("Invoice Items deletion complete");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(errorMessage);
      throw error;
    }

    try {
      const { error } = await supabase.from("invoices").delete().neq("id", 0);
      if (error) {
        toast.error(error.message);
        throw error;
      }

      toast.success("Invoices deletion complete");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(errorMessage);
      throw error;
    }

    try {
      const { error } = await supabase.from("clients").delete().neq("id", 0);
      if (error) {
        toast.error(error.message);
        throw error;
      }

      toast.success("Clients deletion complete");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(errorMessage);
      throw error;
    }

    try {
      const { error } = await supabase.from("products").delete().neq("id", 0);
      if (error) {
        toast.error(error.message);
        throw error;
      }

      toast.success("Products deletion complete");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(errorMessage);
      throw error;
    }

    try {
      const products = await import("@/data/products.json");
      const productsResponse = await supabase.from("products").upsert(products.default);
      console.debug("productsResponse", productsResponse);
      set({ products: products.default as ProductType[] });
      toast.success("Legacy Products inserted");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(errorMessage);
      throw error;
    }

    try {
      const clients = await import("@/data/clients.json");
      const clientsResponse = await supabase.from("clients").upsert(clients.default);
      console.debug("clientsResponse", clientsResponse);
      set({ clients: clients.default as ClientType[] });
      toast.success("Legacy Clients inserted");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(errorMessage);
      throw error;
    }

    try {
      const invoices = await import("@/data/invoices.json");
      const invoicesResponse = await supabase.from("invoices").upsert(invoices.default);
      console.debug("invoicesResponse", invoicesResponse);
      set({ invoices: invoices.default as InvoiceType[] });
      toast.success("Legacy Invoices inserted");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(errorMessage);
      throw error;
    }

    try {
      const invoice_items = await import("@/data/invoice_items.json");
      const invoiceItemsResponse = await supabase
        .from("invoice_items")
        .upsert(invoice_items.default);
      console.debug("invoiceItemsResponse", invoiceItemsResponse);
      toast.success("Legacy Invoice Items inserted");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(errorMessage);
      throw error;
    }

    toast.success("Data reset complete");
  },
  setInvoices: (invoices) => set({ invoices }),
  setClients: (clients) => set({ clients }),
  setProducts: (products) => set({ products }),
  setDrivers: (drivers) => set({ drivers }),
  setTransports: (transports) => set({ transports }),
  setWaybills: (waybills) => set({ waybills }),
  setInitialData: ({ invoices, clients, products, drivers, transports, tenant, user }) =>
    set({ invoices, clients, products, drivers, transports, tenant, user }),
  createInvoice: async ({ invoice_items, ...payload }) => {
    set({
      isCreateInvoiceLoading: true,
      hasCreateInvoiceError: false,
      createInvoiceError: null,
    });
    const { invoices, tenant } = getStore();
    const total = invoice_items.reduce((acc, item) => acc + item.price * item.quantity, 0);
    const lastInvoiceId = Math.max(...invoices.map((invoice) => invoice.id), 0);

    try {
      const { error: invoicesError, data: invoicesData } = await supabase
        .from("invoices")
        .insert({
          ...payload,
          total,
          id: lastInvoiceId + 1,
          series: tenant?.invoice_series,
        })
        .select()
        .single();

      if (invoicesError) {
        set({ hasCreateInvoiceError: true, createInvoiceError: invoicesError.message });
        toast.error(
          `Kuriant sąskaitą įvyko klaida. Bandykite dar kartą.\n${invoicesError.message}`,
        );
        throw invoicesError;
      }

      set({ invoices: [...(invoices ?? []), invoicesData] });

      // Attach created invoice id to each of invoice items
      const items = invoice_items.map((item: InvoiceItemType) => ({
        ...item,
        invoice_id: invoicesData.id,
      }));

      const { error: itemsError } = await supabase.from("invoice_items").insert(items);

      if (itemsError) {
        set({ hasCreateInvoiceError: true, createInvoiceError: itemsError.message });
        toast.error(`Kuriant sąskaitą įvyko klaida. Bandykite dar kartą.\n${itemsError.message}`);
        throw itemsError;
      }

      toast.success("Sąskaita sėkmingai sukurta");
      return {
        id: invoicesData.id,
        code: invoicesData.code,
      };
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasCreateInvoiceError: true, createInvoiceError: errorMessage });
      toast.error(`Kuriant sąskaitą įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isCreateInvoiceLoading: false });
    }
  },
  updateInvoice: async ({ invoice_items, ...payload }) => {
    set({ isUpdateInvoiceLoading: true, hasUpdateInvoiceError: false, updateInvoiceError: null });
    const total = invoice_items.reduce((acc, item) => acc + item.price * item.quantity, 0);

    try {
      const { error: invoicesError, data: invoicesData } = await supabase
        .from("invoices")
        .upsert({ ...payload, total })
        .select()
        .single();

      if (invoicesError) {
        set({ hasUpdateInvoiceError: true, updateInvoiceError: invoicesError.message });
        toast.error(
          `Atnaujinant sąskaitą įvyko klaida. Bandykite dar kartą.\n${invoicesError.message}`,
        );
        throw invoicesError;
      }

      const itemsWithInvoiceId = invoice_items.map((item: InvoiceItemType) => ({
        ...item,
        invoice_id: invoicesData.id,
      }));

      // Split items by whether they already exist in the DB. Only existing
      // items (those with an `id`) may be upserted; items without an `id` are
      // new and must be inserted. Before we started threading `id` through the
      // form state, every edit looked like a "new" item and each save produced
      // duplicate rows in `invoice_items` — this split is what prevents that.
      const newInvoiceItems = itemsWithInvoiceId.filter((item: InvoiceItemType) => !item.id);
      const existingInvoiceItems = itemsWithInvoiceId.filter((item: InvoiceItemType) => !!item.id);

      // Guard against empty writes: Supabase rejects `insert([])` / `upsert([])`
      // on tables with RLS in some configurations, and either way it's a
      // wasted round-trip on the common "edit existing item only" path.
      const { error: itemsInsertError } = newInvoiceItems.length
        ? await supabase.from("invoice_items").insert(newInvoiceItems)
        : { error: null };
      const { error: itemsUpdateError } = existingInvoiceItems.length
        ? await supabase.from("invoice_items").upsert(existingInvoiceItems)
        : { error: null };

      if (itemsInsertError) {
        set({ hasUpdateInvoiceError: true, updateInvoiceError: itemsInsertError.message });
        toast.error(
          `Atnaujinant sąskaitą įvyko klaida. Bandykite dar kartą.\n${itemsInsertError.message}`,
        );
        throw itemsInsertError;
      }

      if (itemsUpdateError) {
        set({ hasUpdateInvoiceError: true, updateInvoiceError: itemsUpdateError.message });
        toast.error(
          `Atnaujinant sąskaitą įvyko klaida. Bandykite dar kartą.\n${itemsUpdateError.message}`,
        );
        throw itemsUpdateError;
      }

      toast.success("Sąskaita sėkmingai atnaujinta");
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasUpdateInvoiceError: true, updateInvoiceError: errorMessage });
      toast.error(`Atnaujinant sąskaitą įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isUpdateInvoiceLoading: false });
    }
  },
  hardRemoveInvoice: async (id) => {
    set({ isRemoveInvoiceLoading: true, hasRemoveInvoiceError: false, removeInvoiceError: null });
    const { invoices } = getStore();

    try {
      const { error } = await supabase.from("invoice_items").delete().eq("invoice_id", id);

      if (error) {
        set({ hasRemoveInvoiceError: true, removeInvoiceError: error.message });
        toast.error(`Trinant sąskaitą įvyko klaida. Bandykite dar kartą.\n${error?.message}`);
        throw error;
      }
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasRemoveInvoiceError: true, removeInvoiceError: errorMessage });
      toast.error(`Trinant sąskaitą įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    }
    try {
      const { error } = await supabase.from("invoices").delete().eq("id", id);

      if (error) {
        set({ hasRemoveInvoiceError: true, removeInvoiceError: error.message });
        toast.error(`Trinant sąskaitą įvyko klaida. Bandykite dar kartą.\n${error?.message}`);
        throw error;
      }

      set({ invoices: invoices.filter((invoice) => invoice.id !== id) });
      toast.success("Sąskaita sėkmingai pašalinta");
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasRemoveInvoiceError: true, removeInvoiceError: errorMessage });
      toast.error(`Trinant sąskaitą įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isRemoveInvoiceLoading: false });
    }
  },
  removeInvoice: async (id) => {
    set({ isRemoveInvoiceLoading: true, hasRemoveInvoiceError: false, removeInvoiceError: null });
    const { invoices } = getStore();

    try {
      const { error } = await supabase.from("invoices").update({ discarded: true }).eq("id", id);

      if (error) {
        set({ hasRemoveInvoiceError: true, removeInvoiceError: error.message });
        toast.error(`Trinant sąskaitą įvyko klaida. Bandykite dar kartą.\n${error?.message}`);
        throw error;
      }

      set({
        invoices: invoices.map((invoice) =>
          invoice.id === id ? { ...invoice, discarded: true } : invoice,
        ),
      });
      toast.success("Sąskaita sėkmingai pašalinta");
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasRemoveInvoiceError: true, removeInvoiceError: errorMessage });
      toast.error(`Trinant sąskaitą įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isRemoveInvoiceLoading: false });
    }
  },
  removeInvoiceItem: async (id) => {
    set({ isRemoveInvoiceLoading: true, hasRemoveInvoiceError: false, removeInvoiceError: null });
    // const { invoices } = getStore();

    try {
      const { error } = await supabase.from("invoice_items").delete().eq("id", id);

      if (error) {
        set({ hasRemoveInvoiceError: true, removeInvoiceError: error.message });
        toast.error(
          `Trinant sąskaitos prekę įvyko klaida. Bandykite dar kartą.\n${error?.message}`,
        );
        throw error;
      }

      toast.success("Sąskaitos prekė sėkmingai pašalinta");
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasRemoveInvoiceError: true, removeInvoiceError: errorMessage });
      toast.error(`Trinant sąskaitos prekę įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isRemoveInvoiceLoading: false });
    }
  },
  updateTenant: async (payload) => {
    set({ isUpdateTenantLoading: true, hasUpdateTenantError: false, updateTenantError: null });
    const { tenant } = getStore();
    if (!tenant) {
      throw new Error("Tenant not found");
    }

    try {
      const { error, data } = await supabase
        .from("tenants")
        .update(payload)
        .eq("id", tenant.id)
        .select()
        .single();

      if (error) {
        set({ hasUpdateTenantError: true, updateTenantError: error.message });
        toast.error(
          `Atnaujinant įmonės duomenis įvyko klaida. Bandykite dar kartą.\n${error.message}`,
        );
        throw error;
      }

      set({ tenant: data });
      toast.success("Įmonės duomenys sėkmingai atnaujinti");
      return data;
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();

      set({ hasUpdateTenantError: true, updateTenantError: errorMessage });
      toast.error(
        `Atnaujinant įmonės duomenis įvyko klaida. Bandykite dar kartą.\n${errorMessage}`,
      );
      throw error;
    } finally {
      set({ isUpdateTenantLoading: false });
    }
  },
  upsertClient: async (payload) => {
    set({ isUpsertClientLoading: true, hasUpsertClientError: false, upsertClientError: null });
    try {
      const { error, data } = await supabase.from("clients").upsert(payload).select().single();

      if (error) {
        set({ hasUpsertClientError: true, upsertClientError: error.message });
        throw error;
      }

      return data;
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();

      set({ hasUpsertClientError: true, upsertClientError: errorMessage });
      throw error;
    } finally {
      set({ isUpsertClientLoading: false });
    }
  },
  upsertProduct: async (payload) => {
    set({ isUpsertProductLoading: true, hasUpsertProductError: false, upsertProductError: null });
    const lastProductId = getStore().products.reduce((acc, product) => {
      const numericId = +product.id;
      if (isNaN(numericId)) return acc;
      return Math.max(acc, numericId);
    }, 0);

    try {
      payload.id = payload.id ?? (lastProductId + 1).toString();
      const { error, data } = await supabase.from("products").upsert(payload).select().single();

      if (error) {
        set({ hasUpsertProductError: true, upsertProductError: error.message });
        toast.error(`Kuriant prekę įvyko klaida. Bandykite dar kartą.\n${error.message}`);
        throw error;
      }

      toast.success("Prekė sėkmingai išsaugota");
      return data;
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasUpsertProductError: true, upsertProductError: errorMessage });
      toast.error(`Kuriant prekę įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isUpsertProductLoading: false });
    }
  },
  openDialog: (dialog: DialogType) =>
    set((state) => {
      return {
        dialogs: [
          ...state.dialogs,
          {
            ...dialog,
            time: new Date(),
          },
        ],
      };
    }),
  closeDialog: (time: Date) =>
    set((state) => {
      return {
        dialogs: state.dialogs.filter((dialog) => dialog.time !== time),
      };
    }),
  createDriver: async (payload) => {
    set({
      isCreateDriverLoading: true,
      hasCreateDriverError: false,
      createDriverError: null,
    });
    const { drivers } = getStore();

    try {
      const { data, error } = await apiCreateDriver(payload);

      if (error) {
        set({ hasCreateDriverError: true, createDriverError: error.message });
        toast.error(`Kuriant vairuotoją įvyko klaida. Bandykite dar kartą.\n${error.message}`);
        throw error;
      }

      set({ drivers: [...drivers, data] });
      toast.success("Vairuotojas sėkmingai sukurtas");
      return data;
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasCreateDriverError: true, createDriverError: errorMessage });
      toast.error(`Kuriant vairuotoją įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isCreateDriverLoading: false });
    }
  },
  updateDriver: async (id, payload) => {
    set({
      isUpdateDriverLoading: true,
      hasUpdateDriverError: false,
      updateDriverError: null,
    });
    const { drivers } = getStore();

    try {
      const { data, error } = await apiUpdateDriver(id, payload);

      if (error) {
        set({ hasUpdateDriverError: true, updateDriverError: error.message });
        toast.error(`Atnaujinant vairuotoją įvyko klaida. Bandykite dar kartą.\n${error.message}`);
        throw error;
      }

      set({
        drivers: drivers.map((driver) => (driver.id === id ? data : driver)),
      });
      toast.success("Vairuotojas sėkmingai atnaujintas");
      return data;
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasUpdateDriverError: true, updateDriverError: errorMessage });
      toast.error(`Atnaujinant vairuotoją įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isUpdateDriverLoading: false });
    }
  },
  deleteDriver: async (id) => {
    set({
      isDeleteDriverLoading: true,
      hasDeleteDriverError: false,
      deleteDriverError: null,
    });
    const { drivers } = getStore();

    try {
      const { error } = await apiDeleteDriver(id);

      if (error) {
        set({ hasDeleteDriverError: true, deleteDriverError: error.message });
        toast.error(`Trinant vairuotoją įvyko klaida. Bandykite dar kartą.\n${error.message}`);
        throw error;
      }

      set({ drivers: drivers.filter((driver) => driver.id !== id) });
      toast.success("Vairuotojas sėkmingai pašalintas");
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasDeleteDriverError: true, deleteDriverError: errorMessage });
      toast.error(`Trinant vairuotoją įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isDeleteDriverLoading: false });
    }
  },
  createTransport: async (payload) => {
    set({
      isCreateTransportLoading: true,
      hasCreateTransportError: false,
      createTransportError: null,
    });
    const { transports } = getStore();

    try {
      const { data, error } = await apiCreateTransport(payload);

      if (error) {
        set({ hasCreateTransportError: true, createTransportError: error.message });
        toast.error(`Kuriant transportą įvyko klaida. Bandykite dar kartą.\n${error.message}`);
        throw error;
      }

      set({ transports: [...transports, data] });
      toast.success("Transportas sėkmingai sukurtas");
      return data;
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasCreateTransportError: true, createTransportError: errorMessage });
      toast.error(`Kuriant transportą įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isCreateTransportLoading: false });
    }
  },
  updateTransport: async (id, payload) => {
    set({
      isUpdateTransportLoading: true,
      hasUpdateTransportError: false,
      updateTransportError: null,
    });
    const { transports } = getStore();

    try {
      const { data, error } = await apiUpdateTransport(id, payload);

      if (error) {
        set({ hasUpdateTransportError: true, updateTransportError: error.message });
        toast.error(`Atnaujinant transportą įvyko klaida. Bandykite dar kartą.\n${error.message}`);
        throw error;
      }

      set({
        transports: transports.map((transport) => (transport.id === id ? data : transport)),
      });
      toast.success("Transportas sėkmingai atnaujintas");
      return data;
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasUpdateTransportError: true, updateTransportError: errorMessage });
      toast.error(`Atnaujinant transportą įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isUpdateTransportLoading: false });
    }
  },
  deleteTransport: async (id) => {
    set({
      isDeleteTransportLoading: true,
      hasDeleteTransportError: false,
      deleteTransportError: null,
    });
    const { transports } = getStore();

    try {
      const { error } = await apiDeleteTransport(id);

      if (error) {
        set({ hasDeleteTransportError: true, deleteTransportError: error.message });
        toast.error(`Trinant transportą įvyko klaida. Bandykite dar kartą.\n${error.message}`);
        throw error;
      }

      set({ transports: transports.filter((transport) => transport.id !== id) });
      toast.success("Transportas sėkmingai pašalintas");
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasDeleteTransportError: true, deleteTransportError: errorMessage });
      toast.error(`Trinant transportą įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isDeleteTransportLoading: false });
    }
  },
  createWaybill: async (payload) => {
    set({
      isCreateWaybillLoading: true,
      hasCreateWaybillError: false,
      createWaybillError: null,
    });
    const { waybills } = getStore();

    try {
      // Convert null values to undefined to match API type expectations
      const sanitizedPayload = {
        invoice_id: payload.invoice_id,
        driver_id: payload.driver_id,
        transport_id: payload.transport_id,
        loading_date: payload.loading_date ?? undefined,
        loading_location: payload.loading_location ?? undefined,
        unloading_date: payload.unloading_date ?? undefined,
        unloading_location: payload.unloading_location ?? undefined,
        cargo_description: payload.cargo_description ?? undefined,
        cargo_weight: payload.cargo_weight ?? undefined,
        cargo_quantity: payload.cargo_quantity ?? undefined,
      };
      const { data, error } = await apiCreateWaybill(sanitizedPayload);

      if (error) {
        set({ hasCreateWaybillError: true, createWaybillError: error.message });
        toast.error(`Kuriant važtaraštį įvyko klaida. Bandykite dar kartą.\n${error.message}`);
        throw error;
      }

      set({ waybills: [...waybills, data] });
      toast.success("Važtaraštis sėkmingai sukurtas");
      return data;
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasCreateWaybillError: true, createWaybillError: errorMessage });
      toast.error(`Kuriant važtaraštį įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isCreateWaybillLoading: false });
    }
  },
  updateWaybill: async (id, payload) => {
    set({
      isUpdateWaybillLoading: true,
      hasUpdateWaybillError: false,
      updateWaybillError: null,
    });
    const { waybills } = getStore();

    try {
      // Convert null values to undefined to match apiUpdateWaybill type expectations
      const cleanedPayload: {
        driver_id?: number;
        transport_id?: number;
        loading_date?: string;
        loading_location?: string;
        unloading_date?: string;
        unloading_location?: string;
        cargo_description?: string;
        cargo_weight?: number;
        cargo_quantity?: number;
      } = {
        ...(payload.driver_id !== undefined && { driver_id: payload.driver_id }),
        ...(payload.transport_id !== undefined && { transport_id: payload.transport_id }),
        ...(payload.loading_date !== undefined &&
          payload.loading_date !== null && { loading_date: payload.loading_date }),
        ...(payload.loading_location !== undefined &&
          payload.loading_location !== null && { loading_location: payload.loading_location }),
        ...(payload.unloading_date !== undefined &&
          payload.unloading_date !== null && { unloading_date: payload.unloading_date }),
        ...(payload.unloading_location !== undefined &&
          payload.unloading_location !== null && {
            unloading_location: payload.unloading_location,
          }),
        ...(payload.cargo_description !== undefined &&
          payload.cargo_description !== null && { cargo_description: payload.cargo_description }),
        ...(payload.cargo_weight !== undefined &&
          payload.cargo_weight !== null && { cargo_weight: payload.cargo_weight }),
        ...(payload.cargo_quantity !== undefined &&
          payload.cargo_quantity !== null && { cargo_quantity: payload.cargo_quantity }),
      };
      const { data, error } = await apiUpdateWaybill(id, cleanedPayload);

      if (error) {
        set({ hasUpdateWaybillError: true, updateWaybillError: error.message });
        toast.error(`Atnaujinant važtaraštį įvyko klaida. Bandykite dar kartą.\n${error.message}`);
        throw error;
      }

      set({
        waybills: waybills.map((waybill) => (waybill.id === id ? data : waybill)),
      });
      toast.success("Važtaraštis sėkmingai atnaujintas");
      return data;
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasUpdateWaybillError: true, updateWaybillError: errorMessage });
      toast.error(`Atnaujinant važtaraštį įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isUpdateWaybillLoading: false });
    }
  },
  deleteWaybill: async (id) => {
    set({
      isDeleteWaybillLoading: true,
      hasDeleteWaybillError: false,
      deleteWaybillError: null,
    });
    const { waybills } = getStore();

    try {
      const { error } = await apiDeleteWaybill(id);

      if (error) {
        set({ hasDeleteWaybillError: true, deleteWaybillError: error.message });
        toast.error(`Trinant važtaraštį įvyko klaida. Bandykite dar kartą.\n${error.message}`);
        throw error;
      }

      set({ waybills: waybills.filter((waybill) => waybill.id !== id) });
      toast.success("Važtaraštis sėkmingai pašalintas");
    } catch (error: Error | unknown) {
      const errorMessage = error?.toString();
      set({ hasDeleteWaybillError: true, deleteWaybillError: errorMessage });
      toast.error(`Trinant važtaraštį įvyko klaida. Bandykite dar kartą.\n${errorMessage}`);
      throw error;
    } finally {
      set({ isDeleteWaybillLoading: false });
    }
  },
}));
